import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  BugReport,
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info,
  Close,
  ContentCopy,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import API from "../../../../../API";

export default function EmailDebugDialog({ 
  open, 
  onClose, 
  username, 
  creatorEmail,
  showToast 
}) {
  const [daysBack, setDaysBack] = useState(7);
  const [debugData, setDebugData] = useState(null);

  const debugMutation = useMutation(
    () => API.blitzAI.debugEmailCheck(username, daysBack),
    {
      onSuccess: (data) => {
        setDebugData(data);
        showToast('Debug check completed', 'success');
      },
      onError: (error) => {
        showToast(error.response?.data?.error || 'Debug check failed', 'error');
      }
    }
  );

  const handleRunDebug = () => {
    debugMutation.mutate();
  };

  const handleCopyQuery = () => {
    if (debugData?.query_info?.query) {
      navigator.clipboard.writeText(debugData.query_info.query);
      showToast('Query copied to clipboard', 'success');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusIcon = () => {
    if (!debugData) return <BugReport />;
    const found = debugData.results?.total_found || 0;
    if (found === 0) return <Warning color="warning" />;
    if (found > 0) return <CheckCircle color="success" />;
    return <Info color="info" />;
  };

  const getStatusColor = () => {
    if (!debugData) return 'default';
    const found = debugData.results?.total_found || 0;
    if (found === 0) return 'warning';
    if (found > 0) return 'success';
    return 'info';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReport />
            <Typography variant="h6">Email Check Debug</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Diagnose email check issues for @{username}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Control Panel */}
        <Card sx={{ mb: 2, bgcolor: 'primary.50' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Days Back"
                  value={daysBack}
                  onChange={(e) => setDaysBack(parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 1, max: 30 } }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={debugMutation.isLoading ? <CircularProgress size={20} /> : <Refresh />}
                  onClick={handleRunDebug}
                  disabled={debugMutation.isLoading}
                >
                  {debugMutation.isLoading ? 'Running...' : 'Run Debug Check'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info Alert */}
        <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Debug Mode:</strong> This check reads Gmail without saving anything to the database.
            Use this to diagnose why email checks return zero results.
          </Typography>
        </Alert>

        {/* Results */}
        {debugData && (
          <Box>
            {/* Status Summary */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {getStatusIcon()}
                  <Typography variant="h6">
                    Status: {debugData.results?.total_found || 0} emails found
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Creator Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {creatorEmail || 'Not connected'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Check
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(debugData.query_info?.last_update)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Query Window
                      </Typography>
                      <Typography variant="body2">
                        {daysBack} days
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Query Details */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Query Details</Typography>
                  <Tooltip title="Copy query">
                    <IconButton size="small" onClick={handleCopyQuery}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Gmail Query
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {debugData.query_info?.query}
                  </Paper>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cutoff Date (UTC)
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(debugData.query_info?.cutoff_date)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Time Window
                      </Typography>
                      <Chip 
                        label={`${debugData.query_info?.days_back || daysBack} days`}
                        color={debugData.query_info?.days_back < 2 ? 'warning' : 'success'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {debugData.query_info?.days_back < 2 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Narrow Time Window:</strong> The query is only checking the last{' '}
                      {debugData.query_info.days_back} day(s). This may explain zero results if the last check was recent.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Database Stats */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Database Statistics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary">
                        {debugData.database_info?.existing_emails_count || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Emails in DB
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="success.main">
                        {debugData.database_info?.last_7_days || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 7 Days
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box textAlign="center">
                      <Typography variant="h4" color={getStatusColor()}>
                        {debugData.results?.total_found || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Found in Gmail
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sample Emails */}
            {debugData.results?.sample_emails && debugData.results.sample_emails.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Sample Emails ({debugData.results.sample_emails.length} of {debugData.results.total_found})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Subject</TableCell>
                          <TableCell>Sender</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debugData.results.sample_emails.map((email, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(email.date)}
                              </Typography>
                            </TableCell>
                            <TableCell>{email.subject || '(No subject)'}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {email.sender}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Zero Results Diagnostics */}
            {debugData.results?.total_found === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Zero Emails Found - Possible Causes:</strong>
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {debugData.query_info?.days_back < 2 && (
                    <li>Time window too narrow ({debugData.query_info.days_back} day(s)) - try increasing days back</li>
                  )}
                  {!creatorEmail && (
                    <li>Gmail not connected for this creator</li>
                  )}
                  {debugData.database_info?.existing_emails_count === 0 && (
                    <li>No emails in database yet - may be first check</li>
                  )}
                  <li>Creator may not have received emails during this period</li>
                  <li>Gmail API authentication may have expired</li>
                </ul>
              </Alert>
            )}
          </Box>
        )}

        {/* No Results Yet */}
        {!debugData && !debugMutation.isLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BugReport sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Click "Run Debug Check" to diagnose email issues
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {debugData && (
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRunDebug}
          >
            Re-run
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}